import { colors } from "shared/theme/color";
import { PackageAnalyticsRow } from "shared/types";

interface PackageCompareCountsChartProps {
  rows: PackageAnalyticsRow[];
}

// The Terjual/Klien Unik legend lives in the caller (PackageComparisonContent),
// inline with this card's heading, not here — see that file for why (keeps
// this card's header the same height as the single-series revenue card's).

// Same local horizontal geometry as PackageCompareRevenueChart (see that
// file for why: long, often-prefix-sharing names don't work under a
// vertical chart's bottom axis, and are wrapped onto up to 2 lines rather
// than truncated) — LABEL_W/ROW_H/LINE_EXTRA all match it exactly so the
// two side-by-side cards land at the same height for the same selection.
const VBOX_W = 520;
const LABEL_W = 220;
// Reserved space for the value text, same reasoning as
// PackageCompareRevenueChart: without it, the row with the largest value
// is the one whose label would need to move inside the bar to avoid
// clipping, which reads as inconsistent styling rather than a deliberate
// guard. Counts are small integers, so this needs far less room than the
// revenue chart's Rupiah figures.
const VALUE_ZONE_W = 28;
const PAD_RIGHT = 8;
const PAD_TOP = 8;
const PAD_BOTTOM = 8;
const ROW_H = 42;
const LINE_EXTRA = 14;
const SUB_BAR_H = 14;
const SUB_BAR_GAP = 3;
const PLOT_W = VBOX_W - LABEL_W - VALUE_ZONE_W - PAD_RIGHT;
const LABEL_MAX_CHARS_PER_LINE = 28;

// See PackageCompareRevenueChart.tsx for the full rationale — identical
// wrapping rule, kept in sync (same LABEL_MAX_CHARS_PER_LINE) so a given
// selection wraps the same way, and the two chart cards' heights match.
const wrapLabel = (name: string): string[] => {
  const words = name.split(" ");
  const lines: string[] = [];
  let current = "";
  let i = 0;
  while (i < words.length && lines.length < 2) {
    const candidate = current ? `${current} ${words[i]}` : words[i];
    if (candidate.length <= LABEL_MAX_CHARS_PER_LINE || !current) {
      current = candidate;
      i++;
    } else {
      lines.push(current);
      current = "";
    }
  }
  if (current) lines.push(current);
  const hasMore = i < words.length;
  const lastIdx = lines.length - 1;
  if (hasMore || (lines[lastIdx]?.length ?? 0) > LABEL_MAX_CHARS_PER_LINE) {
    const base =
      lines[lastIdx].length > LABEL_MAX_CHARS_PER_LINE - 1
        ? lines[lastIdx].slice(0, LABEL_MAX_CHARS_PER_LINE - 1)
        : lines[lastIdx];
    lines[lastIdx] = `${base}…`;
  }
  return lines;
};

// Grouped (side-by-side sub-bars), not stacked like the other two-series
// band charts in this app (RevenueCompositionChart/SessionsCompletedChart)
// — Terjual and Klien Unik don't sum to a meaningful total (one client can
// buy more than once), so stacking them would imply a total that isn't real.
const PackageCompareCountsChart = ({ rows }: PackageCompareCountsChartProps) => {
  const rowHeights = rows.map((r) => (wrapLabel(r.name).length > 1 ? ROW_H + LINE_EXTRA : ROW_H));
  const rowTops: number[] = [];
  rowHeights.reduce((top, h) => {
    rowTops.push(top);
    return top + h;
  }, PAD_TOP);
  const vboxH = rowHeights.reduce((sum, h) => sum + h, 0) + PAD_TOP + PAD_BOTTOM;
  const max = Math.max(...rows.map((r) => Math.max(r.sold, r.unique_clients)), 1) * 1.08;

  const bars = rows.map((r, i) => {
    const rowH = rowHeights[i];
    const rowCenterY = rowTops[i] + rowH / 2;
    const soldY = rowCenterY - SUB_BAR_H / 2 - SUB_BAR_GAP / 2;
    const clientsY = rowCenterY + SUB_BAR_H / 2 + SUB_BAR_GAP / 2;
    const soldW = (r.sold / max) * PLOT_W;
    const clientsW = (r.unique_clients / max) * PLOT_W;
    return { ...r, rowH, rowCenterY, soldY, clientsY, soldW, clientsW, lines: wrapLabel(r.name) };
  });

  const valueLabel = (x: number, y: number, value: number, color: string) => (
    <text x={x + 6} y={y + 3} textAnchor="start" fontSize={10} fontWeight={600} fill={color}>
      {value}
    </text>
  );

  return (
    <svg
      width="100%"
      height={vboxH}
      viewBox={`0 0 ${VBOX_W} ${vboxH}`}
      preserveAspectRatio="xMidYMid meet"
    >
      <line
        x1={LABEL_W}
        x2={LABEL_W}
        y1={PAD_TOP}
        y2={vboxH - PAD_BOTTOM}
        stroke={colors.line}
        strokeWidth={1}
      />

      {bars.map((b) => {
        const oneLine = b.lines.length === 1;
        return (
          <g key={b.package_id}>
            {b.lines.map((line, li) => (
              <text
                key={li}
                x={LABEL_W - 8}
                y={oneLine ? b.rowCenterY + 4 : b.rowCenterY + (li === 0 ? -5 : 8)}
                textAnchor="end"
                fontSize={11}
                fill={colors.ink2}
              >
                {line}
              </text>
            ))}
            <rect
              x={LABEL_W}
              y={b.soldY - SUB_BAR_H / 2}
              width={Math.max(0, b.soldW)}
              height={SUB_BAR_H}
              fill={colors.navy}
            />
            {valueLabel(LABEL_W + b.soldW, b.soldY, b.sold, colors.navy)}
            <rect
              x={LABEL_W}
              y={b.clientsY - SUB_BAR_H / 2}
              width={Math.max(0, b.clientsW)}
              height={SUB_BAR_H}
              fill={colors.ink4}
            />
            {valueLabel(LABEL_W + b.clientsW, b.clientsY, b.unique_clients, colors.ink4)}
          </g>
        );
      })}
    </svg>
  );
};

export default PackageCompareCountsChart;
